
import { ShoppingCategory } from './types';

export const EXPENSE_CATEGORIES = [
  'Housing & Utilities',
  'Food & Daily Needs',
  'Transport & Travel',
  'Health & Personal Care',
  'Fun & Lifestyle',
  'Misc'
];

// Colors based on brand palette: #3EAFD2, #FF9800, #7E57C2, #4CAF50, #F06292, #757575
export const SHOPPING_CATEGORY_COLORS: Record<ShoppingCategory, string> = {
  [ShoppingCategory.SUPERMARKET]: 'border-l-[#4CAF50]',
  [ShoppingCategory.WET_MARKET]: 'border-l-[#F06292]',
  [ShoppingCategory.OTHERS]: 'border-l-[#757575]',
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'zh-HK', name: 'Cantonese' },
  { code: 'zh-CN', name: 'Chinese Simplified' },
  { code: 'zh-TW', name: 'Chinese Traditional' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ko', name: 'Korean' },
  { code: 'ja', name: 'Japanese' },
];

// The source of truth for UI strings.
export const BASE_TRANSLATIONS: Record<string, string> = {
  // Home (formerly Dashboard)
  'dashboard.greeting.morning': 'Good morning',
  'dashboard.greeting.afternoon': 'Good afternoon',
  'dashboard.greeting.evening': 'Good evening',
  'dashboard.family_board': 'Family Board',
  'dashboard.tap_to_pin': 'Tap to pin a note...',
  'dashboard.type_note': 'Type a note for the family...',
  'dashboard.shopping': 'Shopping',
  'dashboard.items_needed': 'Items needed',
  'dashboard.tasks': 'Tasks',
  'dashboard.todo': 'To Do',
  'dashboard.meals': 'Meals',
  'dashboard.planned': 'Planned',
  'dashboard.expenses': 'Expenses',
  'dashboard.this_month': 'This Month',
  'dashboard.up_next': 'Up Next',
  'dashboard.view_schedule': 'View Schedule',
  'dashboard.everyone': 'Everyone',
  'dashboard.eating': 'eating',
  'dashboard.language': 'Language',
  'dashboard.need_help': 'Need Help Getting Started?',
  'dashboard.send_feedback': 'Send Feedback',
  'dashboard.tell_others': 'Tell Others',
  'dashboard.add_family_or_helper': 'Add Family Member\nor Helper',
  'share.app_text': `Hey! I found this great app called Helpy - it's a home management app that helps families and helpers stay organized together.

It handles:
- Meal planning
- Tasks & shopping lists
- Expense tracking
- Family practices & routines
- Helper salary slips

Give it a try:`,
  'dashboard.slots': 'slots',
  'dashboard.member_slots_used': 'member slots used',

  // Filters & Common Tabs
  'filter.all': 'All',
  'filter.today': 'Today',
  'filter.overdue': 'Overdue',
  'filter.later': 'Later',
  'filter.general': 'General',

  // Shopping
  'shopping.title': 'Shopping',
  'shopping.new_item': 'New Item',
  'shopping.item_name': 'Item Name',
  'shopping.unit_placeholder': 'Unit (pkt, kg)',
  'shopping.quick_add': 'Quick Add',
  'shopping.completed': 'Completed',
  'shopping.clear_all': 'Clear All',
  'shopping.no_items': 'No items in list',
  'category.supermarket': 'Supermarket',
  'category.wet_market': 'Wet Market',
  'category.others': 'Others',

  // Tasks
  'tasks.title': 'Tasks',
  'tasks.new_task': 'New Task',
  'tasks.task_name': 'Task Name',
  'tasks.assignees': 'Assignees',
  'tasks.recurrence': 'Does not repeat',
  'tasks.custom_recurrence': 'Custom Recurrence',
  'tasks.repeat_every': 'Repeat every',
  'tasks.repeat_on': 'Repeat on',
  'tasks.ends': 'Ends',
  'tasks.never': 'Never',
  'tasks.on': 'On',
  'tasks.after': 'After',
  'tasks.occurrences': 'occurrences',
  'tasks.once': 'Once',
  'tasks.daily': 'Daily',
  'tasks.weekly': 'Weekly',
  'tasks.biweekly': 'Bi-weekly',
  'tasks.monthly': 'Monthly',
  'tasks.yearly': 'Yearly',
  
  // Tasks Empty States
  'tasks.no_tasks_all': 'No tasks created',
  'tasks.no_tasks_today': 'No tasks due today',
  'tasks.no_tasks_overdue': 'No overdue tasks',
  'tasks.no_tasks_later': 'No upcoming tasks',

  // Day Bubbles (Single Letter)
  'day.letter.sun': 'S',
  'day.letter.mon': 'M',
  'day.letter.tue': 'T',
  'day.letter.wed': 'W',
  'day.letter.thu': 'T',
  'day.letter.fri': 'F',
  'day.letter.sat': 'S',
  
  // Day Short (For Recurrence)
  'day.short.sun': 'Sun',
  'day.short.mon': 'Mon',
  'day.short.tue': 'Tue',
  'day.short.wed': 'Wed',
  'day.short.thu': 'Thu',
  'day.short.fri': 'Fri',
  'day.short.sat': 'Sat',

  // Meals
  'meals.title': 'Meals',
  'meals.day_view': 'Day View',
  'meals.week_view': 'Week View',
  'meals.today': 'Today',
  'meals.back_to_week': 'Back to Current Week',
  'meals.plan_meal': 'Plan meal...',
  'meals.add_dish': 'Add dish',
  'meals.the_dish': 'Dish',
  'meals.whats_for': 'What\'s for',
  'meals.search_youtube': 'Search Recipe in YouTube',
  'meals.who_eating': 'Who is eating?',
  'meals.add_meal': 'Add Meal',
  'meals.save_changes': 'Save Changes',
  'meals.suggest_ai': 'Suggest with AI',
  'meals.group_all': 'All',
  'meals.group_adults': 'Adults',
  'meals.group_kids': 'Kids',
  'meals.view_day': 'Day',
  'meals.view_week': 'Week',

  // Meal Types
  'meal.type.breakfast': 'Breakfast',
  'meal.type.lunch': 'Lunch',
  'meal.type.dinner': 'Dinner',
  'meal.type.snacks': 'Snacks',

  // Meal Audience
  'meals.audience_label': 'This meal is for',
  'meals.audience_all': 'Everyone',
  'meals.audience_adults': 'Adults',
  'meals.audience_kids': 'Kids',

  // Meal RSVP
  'meals.join': 'Join Meal',
  'meals.im_in': "I'm In",
  'meals.im_out': "I'm Out",
  'meals.leave': 'Leave',
  'meals.eating': 'Eating',
  'meals.ill_be_eating': "I'll be eating",
  'meals.no_dish_yet': 'No dish yet',
  'meals.not_for_you': 'Not for you',
  'meals.no_one_yet': 'No one yet',
  'meals.plan_dish': 'Plan Dish',
  'meals.edit_dish': 'Edit dish',
  'meals.add_meal_plan': 'Add Meal Plan',
  'meals.adults_only': 'Adults Only',
  'meals.kids_only': 'Kids Only',
  'meals.tap_to_join': 'Tap to Join',
  'meals.joined': 'Joined',
  'meals.whats_cooking': 'Dish Name',
  'meals.delete_meal': 'Delete Meal',
  'meals.export_pdf': 'Export PDF',
  'meals.meal_plan': 'Meal Plan',

  // Expenses
  'expenses.title': 'Expenses',
  'expenses.total_month': 'Total this month',
  'expenses.scan_receipt': 'Scan Receipt',
  'expenses.analyzing': 'Analyzing Receipt with AI...',
  'expenses.breakdown': 'Spending Breakdown',
  'expenses.manual_entry': 'Manual Entry',

  // Household Info
  'info.title': 'Family Book',
  'info.add_section': 'Add Custom Section',
  'info.edit_info': 'Edit Info',
  'info.new_info': 'New Info',
  'info.category': 'Category',
  'info.title_label': 'Title',
  'info.content_label': 'Content',
  'info.cat.house_rules': 'Home Rules',
  'info.placeholder.category': 'General',
  'info.placeholder.title': 'e.g. WiFi Password',
  'info.placeholder.content': 'Enter details...',

  // Profile
  'profile.title': 'Family Profiles',
  'profile.back': 'Back',
  'profile.add_member': 'Add Family Member',
  'profile.edit_profile': 'Edit Profile',
  'profile.logout': 'Log Out',
  'profile.allergies': 'Allergies & Medical',
  'profile.preferences': 'Preferences',
  'profile.none_added': 'None added',
  'profile.change_photo': 'Change Photo',
  'profile.take_photo': 'Take Photo',
  'profile.choose_library': 'Choose from Library',
  'profile.invite_link': 'Invite Link',
  'profile.copy_link': 'Copy Link',
  'profile.copied': 'Copied!',
  'profile.restart_tutorial': 'Restart Tutorial',
  'profile.add_family': 'Add Family',

  // Onboarding - Common
  'onboarding.welcome': 'Welcome, {name}!',
  'onboarding.next': 'Next',
  'onboarding.finish': 'Finish',
  'onboarding.skip': 'Skip',
  'onboarding.got_it': 'Got it',
  'onboarding.add_member': 'Add Family Member',
  'onboarding.skip_confirm_title': 'Skip Tutorial?',
  'onboarding.skip_confirm_desc': 'You can redo the tutorial anytime by tapping your profile photo and scrolling down to "Tutorial".',
  'onboarding.skip_confirm_no': 'Continue Tutorial',
  'onboarding.skip_confirm_yes': 'Skip for Now',

  // Onboarding - Admin Steps (6 steps)
  'onboarding.admin.1.title': 'Welcome',
  'onboarding.admin.1.desc': 'This is your home command center! First, let\'s add your family members.',
  'onboarding.admin.2.title': 'Add family members',
  'onboarding.admin.2.desc': 'Tap your profile icon.',
  'onboarding.admin.3.title': 'Add family members',
  'onboarding.admin.3.desc': 'Tap Add (+).\nInput their name and send them the invitation link to join the Family.',
  'onboarding.admin.4.title': 'All about your family',
  'onboarding.admin.4.desc': 'Add important information and places here.',
  'onboarding.admin.5.title': 'Home-cooked meals are the best',
  'onboarding.admin.5.desc': 'Set the meal plan and let them know you are eating at home.',
  'onboarding.admin.6.title': 'Got anything important to share with the rest?',
  'onboarding.admin.6.desc': 'Write it down here for everyone to see.',

  // Onboarding - Helper Steps (8 steps)
  'onboarding.helper.1.title': 'Welcome',
  'onboarding.helper.1.desc': 'We\'re glad you\'re here! This is your hub for everything about our home and family.',
  'onboarding.helper.2.title': 'Get to know the family',
  'onboarding.helper.2.desc': 'Here you\'ll find essential details about our home and important places.',
  'onboarding.helper.3.title': 'Get to know the family',
  'onboarding.helper.3.desc': 'This covers our family\'s daily routines and shared practices.',
  'onboarding.helper.4.title': 'Things to buy',
  'onboarding.helper.4.desc': 'This is your shopping list.',
  'onboarding.helper.5.title': 'Things to do',
  'onboarding.helper.5.desc': 'This is your task list.',
  'onboarding.helper.6.title': 'What to cook',
  'onboarding.helper.6.desc': 'This is the family meal plan. View the dishes, see who\'s eating.',
  'onboarding.helper.7.title': 'Add receipt',
  'onboarding.helper.7.desc': 'Add your receipt here.',
  'onboarding.helper.8.title': 'Important note',
  'onboarding.helper.8.desc': 'We put a special and important note here.',

  // Onboarding - Family (Spouse/Kids) Steps (4 steps)
  'onboarding.family.1.title': 'Welcome',
  'onboarding.family.1.desc': 'Love it when family members get together! Let\'s see what has been prepared for you.',
  'onboarding.family.2.title': 'All about your family',
  'onboarding.family.2.desc': 'Add important information and places here.',
  'onboarding.family.3.title': 'Home-cooked meals are the best',
  'onboarding.family.3.desc': 'Set the meal plan and let them know you are eating at home.',
  'onboarding.family.4.title': 'Got anything important to share with the rest?',
  'onboarding.family.4.desc': 'Write it down here for everyone to see.',

  // ToDo (unified Shopping + Tasks)
  'todo.title': 'To Do',
  'todo.shopping': 'Shopping',
  'todo.tasks': 'Tasks',
  'todo.suggested': 'Suggested',
  'todo.previous_completed_tasks': 'Previous Completed Tasks',
  'todo.previous_purchased_items': 'Previous Purchased Items',
  'todo.completed': 'Completed',
  'todo.no_shopping': 'No shopping items yet',
  'todo.no_tasks': 'No tasks yet',
  'todo.add_hint': 'Press Enter to add / tap (+) to set details',
  'todo.category.home_care': 'Home Care',
  'todo.category.family_care': 'Family Care',
  'todo.category.others': 'Others',

  // Nav
  'nav.home': 'Home',
  'nav.todo': 'To Do',
  'nav.meals': 'Meals',
  'nav.expenses': 'Expenses',
  'nav.info': 'Family',
  'nav.profile': 'Profile',
  
  // Common
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.add': 'Add',
  'common.add_and_invite': 'Add and Send Invite Link',
  'common.of': 'of',
  'common.date': 'Date',
  'common.upgrade_to_add_more': 'Upgrade to Add More',
  'profile.family_slots_used': 'family slots used',
  'profile.helper_slots_used': 'helper slots used',
  'profile.member_slots_used': 'member slots used',
  'common.remove': 'Remove',
  'common.confirm_clear': 'Are you sure you want to clear these items?',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.update': 'Update',
  'common.view': 'View',
  'common.start': 'Start',
  'common.all': 'All',
  'common.none': 'None',
  'common.none_listed': 'None listed',
  'common.more': 'more',
  'common.qty': 'Qty',
  'common.unit_pcs': 'pcs',
  'common.unit_kg': 'kg',
  'common.unit_l': 'L',
  'common.eg': 'e.g.',
  'common.eg_milk': 'e.g., Milk',
  'common.eg_clean_bathroom': 'e.g., Clean bathroom',
  'common.eg_wifi_password': 'e.g. WiFi Password',
  'common.where_did_you_spend': 'Where did you spend?',
  'common.store_name': 'Store name',
  'common.enter_details': 'Enter details...',
  'common.add_with_details': 'Add with details',
  'common.sort_by': 'Sort by',
  'common.show_only_mine': 'Show only mine',
  'common.added_date': 'Added Date',
  'common.due_date': 'Due Date',
  'common.newest': 'newest',
  'common.oldest': 'oldest',
  'common.list': 'List',
  'common.summary': 'Summary',
  'common.all_expenses': 'All Expenses',
  'common.total_for_all': 'Total for all expenses',
  'common.total_for_month': 'Total for',
  'common.joined': 'Joined',
  'common.tap_to_join': 'Tap to Join',
  'common.add_allergy': 'Add allergy',
  'common.add_preference': 'Add preference',
  'common.allergy_medical': 'Allergy & Medical',
  'common.settings': 'Settings',
  'common.plan': 'Subscription',
  'common.security': 'Account',
  'common.payment': 'Payment',
  'common.free': 'Free',
  'common.core': 'Core',
  'common.pro': 'Pro',
  'common.monthly': 'Monthly',
  'common.yearly': 'Yearly',
  'common.mo': 'mo',
  'common.yr': 'yr',
  'common.places': 'Places',
  'common.practice': 'Practice',
  'common.training': 'Training',
  'common.tutorial': 'Tutorial',
  'common.helper': 'Helper',
  'common.helpers': 'helpers',
  'common.past': 'Past',
  'common.home': 'Home',
  'common.school': 'School',
  'common.doctor': 'Doctor',
  'common.hospital': 'Hospital',
  'common.shops': 'Shops',
  'common.others': 'Others',
  'common.house_rules': 'Home Rules',
  'common.routine': 'Routine',
  'common.cleaning': 'Cleaning',
  'common.general': 'General',
  'common.category': 'Category',
  'common.title': 'Title',
  'common.content': 'Content',
  'common.does_not_repeat': 'Does not repeat',
  'common.show': 'Show',
  'common.item_name': 'Item Name',
  'common.task_name': 'Task Name',
  'common.unit': 'Unit',
  'common.unit_placeholder': 'pcs, kg, L...',
  'common.brand': 'Brand',
  'common.brand_placeholder': 'Your favorite brand',
  'common.reference_link': 'Reference Link',
  'common.reference_link_placeholder': 'https://...',
  'common.added_date_newest': 'Added Date (newest)',
  'common.added_date_oldest': 'Added Date (oldest)',
  'common.due_date_newest': 'Due Date (newest)',
  'common.due_date_oldest': 'Due Date (oldest)',
  'common.my_items_only': 'My Items Only',
  'common.clear_filters': 'Clear Filters',
  'common.reset_to_default': 'Reset to Default',
  'common.name': 'Name',
  'common.enter_name': 'Enter name',
  'common.current_plan': 'Current Plan',
  'common.price': 'Price',
  'common.popular': 'Popular',
  'common.change_plan': 'Change Plan',
  'common.choose_your_plan': 'Choose Your Plan',
  'common.processing': 'Processing...',
  'common.cancel_subscription': 'Cancel Subscription',
  'common.expires_on': 'Expires On',
  'common.next_payment': 'Next Payment',
  'common.no_active_subscription': 'No active subscription',
  'common.only_admin_can_change': 'Only Admin can make changes to the subscription',
  'common.save_20_percent': 'Save 20%',
  'common.assigned_to': 'Assigned to:',
  'common.pending': 'Pending',
  'common.unassigned': 'Unassigned',
  'common.places_saved': 'places saved',
  'common.done': 'done',
  'dashboard.todays_menu': "Today's Menu",
  'dashboard.items_to_buy': 'items to buy',
  'dashboard.pending': 'pending',
  'todo.category.supermarket': 'Supermarket',
  'todo.category.wet_market': 'Wet Market',
  'expenses.category.housing_utilities': 'Housing & Utilities',
  'expenses.category.food_daily': 'Food & Daily Needs',
  'expenses.category.transport_travel': 'Transport & Travel',
  'expenses.category.health_personal': 'Health & Personal Care',
  'expenses.category.fun_lifestyle': 'Fun & Lifestyle',
  'expenses.category.miscellaneous': 'Misc',
  'info.category.home': 'Home',
  'info.category.school': 'School',
  'info.category.doctor': 'Doctor',
  'info.category.hospital': 'Hospital',
  'info.category.shops': 'Shops',
  'info.category.others': 'Others',
  
  // Profile - Settings & Account
  'profile.invitation_link': 'Invitation Link',
  'profile.share_link_text': 'Share this link with the new member:',
  'profile.invite_title': 'Join Helpy',
  'profile.invite_hi': 'Hi',
  'whatsapp.not_installed': 'WhatsApp is not installed.\n\nWould you like to download it from the App Store?',
  'whatsapp.not_installed_android': 'WhatsApp is not installed.\n\nWould you like to download it from the Play Store?',
  'profile.invite_join_family': 'would like you to join the family in the Helpy app!',
  'profile.invite_app_description': 'Helpy is the home management app connecting families and helpers, organizing meals, tasks, and expenses in one place.',
  'profile.invite_click_below': 'Click below to join:',
  'profile.invite_signup_guidance': 'Signing up is quick:\n- Fastest: Use "Sign Up with Google" (1 click, no verification needed)\n- Or use email: Make sure you have access to the email you use - we\'ll send a 6-digit code to verify. Check your spam/junk folder if you don\'t see it!',
  'profile.invite_access_app': 'Once you sign up, you can always access the app at: app.helpyfam.com',
  'profile.resend_invite': 'Resend Invite',
  'profile.manage_account': 'Manage your account',
  'profile.delete_family_member': 'Delete Family Member',
  'profile.name_label': 'Name',
  'profile.role': 'Role',
  'profile.role_admin': 'Admin',
  'profile.role_spouse': 'Spouse',
  'profile.role_helper': 'Helper',
  'profile.role_child': 'Child',
  'profile.role_other': 'Other',
  'profile.profile_information': 'Profile Information',
  'profile.first_name': 'First Name',
  'profile.last_name': 'Last Name',
  'profile.mobile_number': 'Mobile Number',
  'profile.email_password': 'Email & Password',
  'profile.email_address': 'Email Address',
  'profile.email_managed_by_google': 'Email managed by Google account',
  'profile.current_password': 'Current Password',
  'profile.new_password': 'New Password',
  'profile.notifications': 'Notifications',
  'profile.enable_notifications': 'Enable Notifications',
  'profile.delete_account': 'Delete Account',
  'profile.subscription_info': 'Subscription Information',
  'profile.subscription_canceled': 'Subscription Canceled',
  'profile.what_happens_next': 'What happens next?',
  'profile.access_until_end': 'You\'ll continue to have access to premium features until the end of your current billing period.',
  'profile.revert_to_free': 'After that, your account will automatically revert to the free plan.',
  'profile.resubscribe_anytime': 'You can resubscribe at any time from your profile settings.',
  'profile.helper_salary_info': 'Salary Information',
  'profile.helper_start_date': 'Start Date',
  'profile.helper_base_salary': 'Base Salary (HK$/month)',
  'profile.helper_food_allowance': 'Food Allowance (HK$/month)',
  'profile.helper_other_allowances': 'Other Allowances',
  'profile.add_allowance': 'Add Allowance',
  'profile.total_salary': 'Total Monthly Salary',
  'profile.card_holder': 'Card Holder',
  'profile.card_expires': 'Expires',
  'profile.card_number': 'Card Number',
  'profile.expiry': 'Expiry',
  'profile.cardholder_name': 'Cardholder Name',
  'profile.card_type': 'Card Type',
  'profile.save_payment': 'Save Payment Method',

  // Helper Management
  'helper.statutory_holidays': 'Statutory Holidays',
  'helper.working': 'Working?',
  'helper.payslip_confirmation': 'Payslip Confirmation',
  'helper.employer': 'Employer',
  'helper.helper': 'Helper',
  'helper.sign': 'Sign',
  'helper.signed': 'Signed',
  'helper.salary_not_configured': 'Salary details not configured',
  'helper.input_salary': 'Set Up',
  'helper.missing_start_date': 'Set the start date to generate payslips',
  'helper.missing_salary': 'Set the salary to generate payslips',
  'helper.missing_start_and_salary': 'Set the start date and salary to generate payslips',
  'helper.select_compensation': 'Select Compensation Type',
  'helper.time_in_lieu': 'Time-in-lieu (1 day off)',
  'helper.overtime_pay': 'Overtime Pay',
  'helper.past_holidays': 'Past Holidays',
  'helper.past_payslips': 'Past Payslips',
  'helper.no_helpers': 'No helpers in this household',
  'helper.no_upcoming_holidays': 'No upcoming holidays',
  'helper.no_past_records': 'No past records',
  'helper.no_past_payslips': 'No past payslips',
  'helper.overtime_amount': 'Overtime Pay Amount',
  'helper.amount': 'Amount (HK$)',
  'helper.add_to_payslip_question': "Add this amount to their monthly payslip?",
  'helper.confirm_signature': 'Confirm Signature',
  'helper.sign_warning': "Press to confirm this month's salary. This action CANNOT be reversed.",
  'helper.confirm_sign': 'Confirm & Sign',
  'helper.calculated_amount': 'Calculated Amount',
  'helper.change_amount': 'Change Amount',
  'helper.base_salary': 'Base Salary',
  'helper.food_allowance': 'Food Allowance',
  'helper.other_allowances': 'Other Allowances',
  'helper.other_allowance': 'Other Allowance',
  'helper.overtime': 'Overtime',
  'helper.total': 'Total',
  'helper.new_amount': 'New Total Amount (HK$)',
  'helper.payslip_confirmed': 'Payslip confirmed and locked',
  'helper.manual_amount': 'Manual Amount',
  'helper.amount_overridden': 'Amount has been manually adjusted by Admin',
  'helper.revert_to_calculated': 'Revert to Calculated Amount',
  'helper.management_title': 'Helper Management',
  'helper.upgrade_modal_desc': "Manage your domestic helper's employment records. Track when they work on statutory holidays, record overtime or time-in-lieu, and confirm monthly payslips with digital signatures from both employer and helper.",
  'common.yes': 'Yes',
  'common.no': 'No',

  // Dashboard
  'dashboard.no_meals_today': 'No meals remaining for today',
  'dashboard.dark_mode': 'Dark Mode (BETA)',
  'dashboard.light_mode': 'Light Mode',

  // Expenses
  'expenses.error': 'Error',
  'expenses.no_data': 'No expense data',
  'expenses.upload_photo': 'Upload Photo',
  'expenses.amount': 'Amount',
  'expenses.select_month': 'Select Month',
  'expenses.add_to_start': 'Add an expense to get started',

  // Family (Places/Practice)
  'info.no_places_yet': 'No places yet',
  'info.no_countries_found': 'No countries found',
  'info.add_places_hint': 'Add important places and contacts for your household',
  'info.name_placeholder': 'e.g., City General Hospital',
  'info.address_placeholder': '123 Main St, City',
  'info.note_placeholder': 'Any additional details...',

  // ToDo
  'todo.tap_to_add': 'Tap above to add one',

  // Auth & Loading
  'auth.tidying_up': 'Tidying things up...',
  'auth.please_wait': 'Please wait a moment',
  'auth.setting_up': 'Setting up your account',
  'auth.almost_ready': 'Almost ready',
  'auth.sign_up': 'Sign Up',
  'auth.sign_in': 'Sign In',
  'auth.sign_in_again': 'Sign In Again',
  'auth.sign_up_email': 'Sign Up with Email',
  'auth.sign_up_google': 'Sign Up with Google',
  'auth.no_account': "Don't have an account?",
  'auth.already_have_account': 'Already have an account?',
  'auth.back_to_sign_in': 'Back to Sign In',
  'auth.create_account': 'Create Account',
  'auth.creating_account': 'Creating Account...',
  'auth.see_features': 'See Helpyfam Features',
  'auth.quote': "\"I just want you to know\nI'm real grateful you're here\"",
  'auth.quote_author': 'Aibileen Clark, The Help',
  'auth.or_continue_email': 'Or continue with email',
  'auth.processing': 'Processing...',
  'auth.verification_failed': 'Verification failed. Please try again.',

  // Invite
  'invite.loading': 'Loading invitation...',
  'invite.completing': 'Completing invitation...',
  'invite.welcome': 'Welcome!',
  'invite.error_title': 'Invitation Error',
  'invite.invalid_or_expired': 'This invitation is invalid or has expired.',
  'invite.go_home': 'Go to Home',
  'invite.verify_email_title': 'Verify Your Email',
  'invite.code_sent_to': 'We sent a code to',
  'invite.verification_code': 'Verification Code',
  'invite.verifying': 'Verifying...',
  'invite.verify_email': 'Verify Email',
  'invite.added_as': "You'll be added as:",
  'invite.invited_to_join': "You've been invited to join",
  'invite.household_suffix': "'s household",

  // Placeholders
  'placeholder.search_country': 'Search country...',
  'placeholder.mobile_number': 'Mobile number',
  'placeholder.mm_yy': 'MM/YY',
  'placeholder.name_on_card': 'Name on card',
  'placeholder.enter_code': 'Enter 6-digit code',
  'placeholder.first_name': 'John',
  'placeholder.last_name': 'Doe',
  'placeholder.enter_password': 'Enter password',
  'placeholder.min_8_chars': 'At least 8 characters',
  'placeholder.email_example': 'john@example.com',
  'placeholder.password_hint': 'Must be at least 8 characters',

  // Common - additional
  'common.saving': 'Saving...',
  'common.items': 'items',
  'common.share': 'Share',
  'common.upgrade_plan': 'Upgrade Plan',
  'common.go_back': 'Go Back',
  'common.deleting': 'Deleting...',

  // Language & Translation
  'language.ai_disclaimer': 'Translation provided by AI. For accuracy, please refer to the original language version if in doubt.',

  // Meals - additional
  'meals.hungry_no_menu': "Someone's joining. No menu yet",

  // Subscription & Payment
  'subscription.payment_success': 'Payment successful! Your subscription is being updated...',
  'subscription.change_plan': 'Change Plan',
  'subscription.choose_plan': 'Choose Your Plan',
  'subscription.admin_only': 'Only Admin can make changes to the subscription',
  'subscription.active_until': 'Your subscription is active until',
  'subscription.canceled_success': 'Your subscription has been successfully canceled.',
  'subscription.confirm_cancel': 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',

  // Confirmation Dialogs
  'confirm.delete_member': 'Are you sure you want to delete this family member? This action cannot be undone.',
  'confirm.delete_account': 'Are you sure you want to delete your account? This change will be permanent.',
  'confirm.delete_account_final': 'Are you sure you want to delete? After deletion it will be immediate.',
  'confirm.clear_completed': 'Delete all completed items? This action cannot be undone.',
  'confirm.delete_expense': 'Are you sure you want to delete this receipt/expense?',

  // Expenses - additional
  'expenses.add_expense': 'Add Expense',
  'expenses.enter_expense': 'Enter Expense',
  'expenses.confirm_receipt': 'Confirm Receipt',

  // Maps
  'maps.choose_app': 'Choose your preferred maps app',

  // Expenses - Form Labels & Buttons
  'expenses.shop_name': 'Shop Name',
  'expenses.date': 'Date',
  'expenses.enter_manually': 'Enter Manually',
  'expenses.or': 'or',
  'expenses.tap_to_zoom': 'Tap to zoom',
  'expenses.no_receipt_image': 'No receipt image',
  'expenses.no_expenses_yet': 'No expenses yet',
  'expenses.no_expenses_month': 'No expenses in',
  'expenses.tap_add_first': 'Tap + to add your first expense',
  'expenses.go_current_month': 'Go to Current Month',
  'expenses.show_all_expenses': 'Show All Expenses',
  'expenses.yes_delete': 'Yes, delete',
  'expenses.in_currency': 'in',
  'expenses.receipt_scanner': 'Receipt Scanner',
  'expenses.scanner_locked_desc': 'Upgrade to scan or upload receipts',
  'expenses.how_much': 'Amount',
  'expenses.where_spend': 'Where did you spend?',
  'expenses.merchant_placeholder': "e.g., Wellcome, McDonald's",
  'expenses.from_photos': 'From Photos',
  'expenses.receipt_scanned': 'Receipt scanned successfully.',
  'expenses.adjust_details': 'You can still manually adjust the details below if needed.',
  'expenses.edit_expense': 'Edit Expense',
  'expenses.expense_details': 'Expense Details',
  'expenses.delete_expense': 'Delete Expense',

  // Expenses - Image Viewer
  'expenses.double_tap_reset': 'Double tap to reset',
  'expenses.drag_to_pan': 'Drag to pan',
  'expenses.pinch_to_zoom': 'Pinch to zoom',
  'expenses.double_tap_zoom': 'Double tap to zoom',
  'expenses.tap_outside_close': 'Tap outside to close',

  // House Routine / Practice Categories
  'routine.category.house_rules': 'Home Rules',
  'routine.category.routine': 'Routine',
  'routine.category.cooking': 'Cooking',
  'routine.category.child_care': 'Child Care',
  'routine.category.cleaning': 'Cleaning',
  'routine.category.grocery': 'Grocery',
  'routine.category.laundry': 'Laundry',
  'routine.category.pet_care': 'Pet Care',
  'routine.category.safety': 'Safety',
  'routine.category.utilities': 'Utilities',
  'routine.category.helper_care': 'Helper Care',
  'routine.category.others': 'Others',

  // Family (Places/Practice) - Form Labels
  'info.address': 'Address',
  'info.phone_number': 'Phone Number',
  'info.note': 'Note',
  'info.tap_see_more': 'tap to see more',
  'info.add_new_info': 'Add New Info',
  'info.add_new_place': 'Add New Place',
  'info.edit_place': 'Edit Place',
  'info.name_of_the_place': 'Place Name',
  'info.place_placeholder': 'e.g., City General Hospital',
  'info.delete_place': 'Delete Place',
  'info.no_practice_yet': 'No practices yet',
  'info.add_practice_hint': 'Add household practices and instructions',
  'info.edit_routine': 'Edit Routine',
  'info.add_routine': 'Add Routine',
  'info.edit_practice': 'Edit Practice',
  'info.add_practice': 'Add Practice',
  'info.name_of_the_practice': 'Practice Name',
  'info.practice_placeholder': 'e.g., How to make the bed',
  'info.instructions_notes': 'Instructions / Notes',
  'info.delete_practice': 'Delete Practice',
  'info.custom_category': 'Custom Category Name',
  'info.custom_category_placeholder': 'Enter custom category',
  'info.routine_name_placeholder': 'e.g., How to make the bed',
  'info.routine_note_placeholder': 'Enter the instructions, steps, or details...',
  'info.no_note': 'No details provided.',

  // ToDo - Form Labels
  'todo.add_item': 'Add item...',
  'todo.add_task': 'Add task...',
  'todo.edit_shopping_item': 'Edit Shopping Item',
  'todo.edit_task': 'Edit Task',
  'todo.add_shopping_item': 'Add Shopping Item',
  'todo.due_date_time': 'Due Date & Time',
  'todo.select_date_time': 'Select date & time',
  'todo.repeat': 'Repeat',
  'todo.assign_to': 'Assign to',
  'todo.clear_all': 'Clear All',
  'todo.you': 'You',
  'todo.what_do_you_need': 'Shopping Item Name',
  'todo.what_needs_to_be_done': 'Task Name',
  'todo.delete_item': 'Delete Item',
  'todo.delete_task': 'Delete Task',

  // ToDo - Recurrence Text
  'recurrence.repeats_every_day': 'Repeats every day',
  'recurrence.every': 'Every',
  'recurrence.weekly': 'Weekly',
  'recurrence.on_the': 'On the',
  'recurrence.of_each_month': 'of each month',
  'recurrence.monthly': 'Monthly',

  // Common - Additional
  'common.you': 'You',
  'common.deleted_user': 'Deleted User',
  'common.add_item': 'Add Item',
  'common.add_task': 'Add Task',
  'common.back': 'Back',
  'common.email': 'Email',
  'common.password': 'Password',
  'common.verify': 'Verify',

  // Profile - Additional
  'profile.delete_member': 'Delete member',

  // Removed from Household Screen
  'removed.title': 'Removed from Household',
  'removed.subtitle': 'You have been removed from your household',
  'removed.explanation_title': 'What happened?',
  'removed.explanation': 'The household administrator has removed you from the household. Your Helpy account is still active, but you are no longer part of any household.',
  'removed.options_title': 'What would you like to do?',
  'removed.create_new': 'Create a new household',
  'removed.create_new_desc': 'Start fresh with your own household',
  'removed.creating': 'Creating household...',
  'removed.delete_account': 'Delete my account permanently',
  'removed.delete_account_desc': 'Remove all your data from Helpy',
  'removed.delete_confirm_title': 'Delete Account?',
  'removed.delete_confirm_message': 'This action cannot be undone. Your account and all associated data will be permanently deleted.',
  'removed.delete_permanently': 'Delete Permanently',
  'removed.contact_admin': 'If you believe this was done in error, please contact your household administrator.',

  // Meals - Additional
  'meals.meal_type': 'Meal Type',

  // Dashboard - Connection Status
  'dashboard.realtime_active': 'Real-time sync active',
  'dashboard.connecting': 'Connecting...',
  'dashboard.disconnected': 'Disconnected - tap to reconnect',

  // Expenses - Additional
  'expenses.all_expenses': 'All Expenses',
  'expenses.error_invalid_amount': 'Please enter a valid amount',

  // Common - Additional
  'common.na': 'N/A',
  'common.adding': 'Adding...',

  // Error Messages - Item Operations (user-friendly, non-technical)
  // Format: "Couldn't [action]. Please check your connection and try again."
  'error.add_item': "Couldn't save. Please check your connection and try again.",
  'error.update_item': "Couldn't update. Please check your connection and try again.",
  'error.delete_item': "Couldn't delete. Please check your connection and try again.",
  'error.delete_items': "Couldn't delete. Please check your connection and try again.",
  
  // Error Messages - Meals
  'error.add_meal': "Couldn't save meal. Please check your connection and try again.",
  'error.update_meal': "Couldn't update meal. Please check your connection and try again.",
  'error.delete_meal': "Couldn't delete meal. Please check your connection and try again.",
  'error.save_meal': "Couldn't save meal. Please check your connection and try again.",
  
  // Error Messages - Family Board
  'error.save_notes': "Couldn't save notes. Please check your connection and try again.",
  'error.delete_notes': "Couldn't delete notes. Please check your connection and try again.",
  
  // Error Messages - Places & Practices
  'error.save_place': "Couldn't save. Please check your connection and try again.",
  'error.delete_place': "Couldn't delete. Please check your connection and try again.",
  'error.save_practice': "Couldn't save. Please check your connection and try again.",
  'error.delete_practice': "Couldn't delete. Please check your connection and try again.",
  
  // Error Messages - User Operations
  'error.add_user': "Couldn't add member. Please check your connection and try again.",
  'error.generate_invite': "Couldn't generate invite link. Please check your connection and try again.",
  'error.select_image': 'Please select an image file',
  'error.image_too_large': 'Image size must be less than 5MB',
  'error.upload_image': "Couldn't upload image. Please check your connection and try again.",
  'error.cancel_subscription': "Couldn't cancel subscription. Please check your connection and try again.",
  'error.delete_account_unable': "Couldn't delete account. Please check your connection and try again.",
  'error.delete_account': "Couldn't delete account. Please check your connection and try again.",
  'error.export_pdf': "Couldn't export PDF. Please check your connection and try again.",
  'error.helper_limit_title': 'Helper Limit Reached',
  'error.family_limit_title': 'Family Member Limit Reached',
  'error.upgrade_to_add_more': 'Upgrade your plan to add more members to your household.',
  
  // Error Messages - Helper Management
  'error.save_contract': "Couldn't save employment details. Please check your connection and try again.",
  'error.delete_slip': "Couldn't delete salary slip. Please check your connection and try again.",
  'error.sign_slip': "Couldn't sign. Please check your connection and try again.",
  'error.load_data': "Couldn't load data. Please check your connection and try again.",

  // Settings - Push Notifications
  'settings.push_not_supported': 'Not supported in this browser',
  'settings.push_blocked': 'Blocked - enable in browser settings',
  'settings.push_description': 'Get notified when family adds items',

  // Notification Status Messages
  'notifications.child': "Children accounts don't receive notifications.",
  'notifications.off': 'Notifications off.',
  'notifications.off_step1': 'Enable in',
  'notifications.off_step1_settings': 'Settings',
  'notifications.off_step1_below': 'below',
  'notifications.tap': 'Tap',
  'notifications.allow': 'Allow',
  'notifications.if_asked': 'if asked',
  'notifications.incomplete': 'Notification setup is incomplete.',
  'notifications.ask_to': 'Ask {name} to:',
  'notifications.step_add_home': 'Add to Home Screen (iPhone/Android)',
  'notifications.step_enable': 'Enable Notification in',
  'notifications.ready': 'Notifications active.',
  
  // Notification Error Messages (for modal alerts)
  'notifications.blocked_title': 'Notifications Blocked',
  'notifications.blocked': 'Notifications are blocked. Please enable them in your browser settings (tap the lock icon in the address bar).',
  'notifications.try_again_title': 'Permission Required',
  'notifications.try_again': 'Please try again and tap "Allow" when prompted.',
  'notifications.setup_failed_title': 'Setup Failed',
  'notifications.setup_failed': 'Failed to enable notifications. Please try again.',

  // Plan Features - Unified feature matrix
  // Plan Section Titles
  'plan.section.users': 'Users',
  'plan.section.basic_features': 'Basic Features',
  'plan.section.expenses': 'Expenses',
  'plan.section.helper_management': 'Helper',
  
  // Basic Features
  'plan.feature.home': 'Home (Family Board & Widgets)',
  'plan.feature.todo': 'To Do (Tasks & Shopping)',
  'plan.feature.meal_planning': 'Meal Planning',
  'plan.feature.family_info': 'Family Info',
  'plan.feature.ai_translations': 'AI Translations',
  
  // Plan Features
  'plan.feature.family_members': 'Family members',
  'plan.feature.helpers': 'Helpers',
  'plan.feature.manual_expenses': 'Add expenses manually',
  'plan.feature.manual_expenses_desc': 'Enter amount, category, shop, and date quickly.',
  'plan.feature.ai_scan': 'AI receipt scanning',
  'plan.feature.ai_scan_desc': 'Take or upload receipt photo and automatically capture the details.',
  'plan.feature.spending_summary': 'Monthly spending summary',
  'plan.feature.spending_summary_desc': 'Pie charts show category totals and percentages.',
  'plan.feature.helper_records': 'Helper details & salary slip',
  'plan.feature.helper_records_desc': 'Manage the helper\'s salary details and generate digital payslips.',
  
  // Plan Trial Labels (Free plan trial features)
  'plan.trial.5_scans': '5 free scans',
  'plan.trial.14_days': '14 days trial',
  'plan.trial.1_signature': '1 free salary slip',
  
  // Plan Features - Test (for Stripe testing)
  'plan.feature.test_desc': 'Test plan for payment testing',
  'common.test': 'Test',

  // Household Switch Modal
  'household.already_member': 'Already a Member',
  'household.already_member_of': 'You are already a member of',
  'household.possessive_household': "'s household",
  'household.invited_to_join': "You've been invited to join:",
  'household.switch_to_this': 'Switch to This Household',
  'household.stay_in_current': 'Stay in Current Household',
  'household.only_one_at_time': 'You can only belong to one household at a time',

  // Feedback / Support
  'feedback.title': 'Feedback',
  'feedback.description': 'Send us your feedback, questions, or report issues',
  'feedback.new_ticket': 'New Message',
  'feedback.subject': 'Subject',
  'feedback.subject_placeholder': 'Brief description of your feedback',
  'feedback.message': 'Message',
  'feedback.message_placeholder': 'Tell us more...',
  'feedback.send': 'Send',
  'feedback.sending': 'Sending...',
  'feedback.reply_placeholder': 'Type your reply...',
  'feedback.no_tickets': 'No messages yet',
  'feedback.no_tickets_hint': 'Tap the button above to send us feedback',
  'feedback.status.open': 'Open',
  'feedback.status.in_progress': 'In Progress',
  'feedback.status.resolved': 'Resolved',
  'feedback.status.closed': 'Closed',
  'feedback.mark_resolved': 'Mark as Resolved',
  'feedback.reopen': 'Reopen',
  'feedback.admin_view': 'All Household Messages',
  'feedback.superadmin_view': 'All Messages (All Households)',
  'feedback.your_messages': 'Your Messages',
  'feedback.from': 'From',
  'feedback.admin_badge': 'Admin',
  'feedback.you_badge': 'You',
  'feedback.delete_confirm': 'Are you sure you want to delete this conversation?',

  // User Guide
  'guide.title': 'User Guide',
  'guide.welcome_title': 'Welcome to Helpy',
  'guide.welcome_desc': 'Your household command center for managing tasks, meals, expenses, and household info - all in one place.',
  'guide.your_role': 'Your Role',
  'guide.getting_started': 'Getting Started',
  'guide.features': 'Features',
  'guide.your_plan': 'Subscription',
  'guide.tips': 'Tips & Best Practices',
  'guide.faq': 'FAQ',
  'guide.need_help': 'Need Help?',
  
  // User Guide - Home
  'guide.dashboard_title': 'Home',
  'guide.dashboard_desc': 'Your home screen shows a quick overview of everything: shopping items needed, pending tasks, upcoming meals, and monthly expenses.',
  'guide.family_board_title': 'Family Board',
  'guide.family_board_desc': 'Pin notes for your whole family to see. Tap to edit and share reminders, announcements, or instructions.',
  
  // User Guide - To-Do
  'guide.todo_title': 'To-Do',
  'guide.todo_desc': 'Manage shopping lists and household tasks in one place.',
  'guide.shopping_title': 'Shopping',
  'guide.shopping_desc': 'Add items with quantity, assign to family members or your helper, and organize by location.',
  'guide.tasks_title': 'Tasks',
  'guide.tasks_desc': 'Create tasks with due dates, set recurrence for repeating chores, and assign to specific people.',
  
  // User Guide - Meals
  'guide.meals_title': 'Meals',
  'guide.meals_desc': 'Plan breakfast, lunch, dinner, and snacks for your family.',
  'guide.meals_rsvp': 'Family members can RSVP to meals so you know who will be eating.',
  'guide.meals_audience': 'Set meals for everyone, adults only, or kids only.',
  'guide.meals_ai': 'Tap the sparkle icon to get AI-powered meal suggestions.',
  
  // User Guide - Expenses
  'guide.expenses_title': 'Expenses',
  'guide.expenses_desc': 'Track household spending with categories and visual breakdowns.',
  'guide.expenses_manual': 'Add expenses manually with amount, category, merchant, and date.',
  'guide.expenses_scan': 'Scan receipts with AI to automatically extract total, merchant, and date.',
  'guide.expenses_chart': 'View spending breakdown by category in a visual pie chart.',
  
  // User Guide - Family
  'guide.info_title': 'Family',
  'guide.info_desc': 'Store important places, house routines, and manage helpers.',
  'guide.places_title': 'Places',
  'guide.places_desc': 'Save important addresses like home, school, doctor, hospital, and shops.',
  'guide.routines_title': 'Practice',
  'guide.routines_desc': 'Document house rules, schedules, cleaning instructions, and emergency procedures.',
  'guide.helper_mgmt_title': 'Helper',
  'guide.helper_mgmt_desc': 'Manage the helper\'s salary details and generate digital payslips.',
  
  // User Guide - Languages
  'guide.languages_title': 'Multi-Language Support',
  'guide.languages_desc': 'Helpy automatically translates content. You can change your language anytime from the dashboard.',
  
  // User Guide - Notifications
  'guide.notifications_title': 'Push Notifications',
  'guide.notifications_desc': 'Get notified when new tasks, meals, or expenses are added. Enable in Profile > Settings.',
  
  // User Guide - Role Descriptions
  'guide.role_admin_title': 'Admin',
  'guide.role_admin_desc': 'The household owner with full control',
  'guide.role_spouse_title': 'Spouse',
  'guide.role_spouse_desc': 'Partner with nearly full access',
  'guide.role_helper_title': 'Helper',
  'guide.role_helper_desc': 'Household staff with task-focused access',
  'guide.role_child_title': 'Child',
  'guide.role_child_desc': 'Profile only family members - not actual app users',
  'guide.role_other_title': 'Other',
  'guide.role_other_desc': 'Extended family or guest with full access',

  // User Guide - Roles Section
  'guide.roles_title': 'Roles',
  'guide.roles_desc': 'Helpy has different roles with different permissions. Learn what each role can do.',
  'guide.you': 'You',
  'guide.what_you_can_do': 'What they can do:',
  'guide.what_you_cant_do': "What they can't do:",
  'guide.full_access': 'Full access - no restrictions',
  
  // User Guide - Admin Abilities
  'guide.admin_ability_1': 'Add and manage family members',
  'guide.admin_ability_2': 'Invite helpers with secure links',
  'guide.admin_ability_3': 'Manage subscription and billing',
  'guide.admin_ability_4': 'Edit the Family Board',
  'guide.admin_ability_5': 'Add, edit, and delete all items',
  'guide.admin_ability_6': 'View all expenses and spending summaries',
  'guide.admin_ability_7': 'Set up places and practice',
  'guide.admin_ability_8': 'Delete the household account',
  
  // User Guide - Spouse Abilities
  'guide.spouse_ability_1': 'Add and manage family members',
  'guide.spouse_ability_2': 'Invite helpers with secure links',
  'guide.spouse_ability_3': 'Edit the Family Board',
  'guide.spouse_ability_4': 'Add, edit, and delete all items',
  'guide.spouse_ability_5': 'View all expenses and spending summaries',
  'guide.spouse_ability_6': 'Set up places and practice',
  'guide.spouse_restriction_1': 'Manage subscription or billing',
  'guide.spouse_restriction_2': 'Delete the household account',
  
  // User Guide - Helper Abilities
  'guide.helper_ability_1': 'Add and complete shopping items and tasks',
  'guide.helper_ability_2': 'View meal plans and RSVP for meals',
  'guide.helper_ability_3': 'Enter and view your own expenses',
  'guide.helper_ability_4': 'View places and practice',
  'guide.helper_ability_5': 'Sign payslips digitally',
  'guide.helper_restriction_1': 'Delete items (shopping, tasks, meals)',
  'guide.helper_restriction_2': 'Add or edit places and practice',
  'guide.helper_restriction_3': "View other people's expenses",
  'guide.helper_restriction_4': 'View spending summaries',
  'guide.helper_restriction_5': 'Edit the Family Board',
  'guide.helper_restriction_6': 'Manage family members',
  'guide.helper_restriction_7': 'Access subscription settings',
  
  // User Guide - Child Profile (Profile-only role)
  'guide.this_profile_is_for': 'This profile is for:',
  'guide.child_profile_1': "Meal planning (who's eating)",
  'guide.child_profile_2': 'Tracking allergies and preferences',
  'guide.child_profile_3': 'Showing in the family list',
  'guide.child_note': "Children don't log in - add them directly for household planning. The invitation link will not be generated.",
  
  // User Guide - Other Role Abilities
  'guide.other_ability_1': 'Add and manage family members',
  'guide.other_ability_2': 'Edit the Family Board',
  'guide.other_ability_3': 'Add, edit, and delete all items',
  'guide.other_ability_4': 'View all expenses and spending summaries',
  'guide.other_ability_5': 'Set up places and practice',
  'guide.other_restriction_1': 'Manage subscription or billing',
  'guide.other_restriction_2': 'Delete the household account',
  
  // User Guide - Plan Comparison
  'guide.plan_comparison': 'Plan Features',
  'guide.plan_feature': 'Feature',
  'guide.plan_family_members': 'Family Members',
  'guide.plan_helpers': 'Helpers',
  'guide.plan_receipt_scan': 'AI Receipt Scanning',
  'guide.plan_helper_mgmt': 'Helper',
  'guide.plan_view_plans': 'View Plans',
  'guide.plan_included': 'Included',
  'guide.plan_not_included': 'Not included',
  
  // User Guide - Tips
  'guide.tip_1_title': 'Assign to Helper by Default',
  'guide.tip_1_desc': 'When you add a task, it auto-assigns to your helper if you have one.',
  'guide.tip_2_title': 'Use Recurrence',
  'guide.tip_2_desc': 'Set daily, weekly, or monthly tasks to never forget routine chores.',
  'guide.tip_3_title': 'Pin Important Notes',
  'guide.tip_3_desc': 'The Family Board is visible to everyone - perfect for reminders.',
  'guide.tip_4_title': 'Plan Meals Ahead',
  'guide.tip_4_desc': 'Use Week View to batch-plan all meals for the week.',
  'guide.tip_5_title': 'Keep Places Updated',
  'guide.tip_5_desc': 'Helpers need quick access to school, doctor, and emergency info.',
  
  // User Guide - FAQ
  'guide.faq_1_q': 'How do I invite a helper?',
  'guide.faq_1_a': 'Go to Profile, tap Add Family Member, select Helper role, and share the invite link.',
  'guide.faq_2_q': 'How do I change my subscription?',
  'guide.faq_2_a': 'Go to Profile > Settings > Subscription to view and change your plan.',
  'guide.faq_3_q': 'Why can\'t my helper edit certain things?',
  'guide.faq_3_a': 'Helpers have view-only access to some features to protect family privacy.',
  'guide.faq_4_q': 'How do I enable notifications?',
  'guide.faq_4_a': 'Go to Profile > Settings and toggle on Push Notifications.',
  'guide.faq_5_q': 'Can I use Helpy in my language?',
  'guide.faq_5_a': 'Yes! Tap the language button on the dashboard to switch between supported languages.',
  
  // User Guide - Footer
  'guide.feedback_cta': 'Have a question? Tap Feedback in Settings.',
  
  // User Guide - Helper Access Overview (for Admin/Spouse)
  'guide.helper_access_title': 'Helper Access Overview',
  'guide.helper_access_desc': 'Understand exactly what your helper can and cannot access in Helpy.',
  'guide.helper_can_see': 'What Helper CAN See',
  'guide.helper_can_do': 'What Helper CAN Do',
  'guide.helper_cannot_see': 'What Helper CANNOT See',
  'guide.helper_cannot_do': 'What Helper CANNOT Do',
  
  // What Helper CAN See
  'guide.helper_see_1': 'Shopping lists and items',
  'guide.helper_see_2': 'Task lists and assignments',
  'guide.helper_see_3': 'Meal plans and schedules',
  'guide.helper_see_4': 'Places (home, school, doctor, etc.)',
  'guide.helper_see_5': 'House routines and instructions',
  'guide.helper_see_6': 'Family member names and avatars',
  'guide.helper_see_7': 'Their own salary and payslip details',
  'guide.helper_see_8': 'Expenses they entered themselves',
  
  // What Helper CAN Do
  'guide.helper_do_1': 'Complete shopping items (mark as bought)',
  'guide.helper_do_2': 'Complete tasks (mark as done)',
  'guide.helper_do_3': 'RSVP to meals',
  'guide.helper_do_4': 'Sign their own payslips digitally',
  'guide.helper_do_5': 'Edit their own profile information',
  
  // What Helper CANNOT See
  'guide.helper_no_see_1': 'Expenses created by other family members',
  'guide.helper_no_see_2': 'Receipt images and scans',
  'guide.helper_no_see_3': 'Monthly totals and spending summaries',
  'guide.helper_no_see_4': 'Subscription and billing information',
  'guide.helper_no_see_5': 'Other family members\' allergies and preferences',
  
  // What Helper CANNOT Do
  'guide.helper_no_do_1': 'Edit or delete the Family Board',
  'guide.helper_no_do_2': 'Delete shopping items or tasks',
  'guide.helper_no_do_3': 'Add, edit, or delete expenses',
  'guide.helper_no_do_4': 'Add or remove family members',
  'guide.helper_no_do_5': 'Edit other people\'s profiles',
  'guide.helper_no_do_6': 'Access subscription or billing settings',
  'guide.helper_no_do_7': 'Clear completed items',
  
  // Helper Privacy Note
  'guide.helper_privacy_note': 'Your financial information and family details remain private. Helpers only see what they need to help manage your household.',

  // Expenses - Upgrade Modal
  'expenses.summary_title': 'Monthly Summary',
  'expenses.summary_upgrade_desc': 'Get insights into your spending habits. View your expenses broken down by category with visual charts, and track your monthly totals at a glance.',
  'common.upgrade': 'Upgrade',
  'common.to_add_more': 'to add more',

  // Subscription - Referral & Downgrade
  'subscription.invalid_referral_code': 'Invalid or expired referral code',
  'subscription.confirm_downgrade_free': 'Are you sure you want to downgrade to the Free plan? Your subscription will be canceled immediately and you will lose access to premium features.',
  'subscription.downgrade_success': 'Your subscription has been canceled. You are now on the Free plan.',
  'subscription.downgrade_to_free_title': 'Cancel Subscription',
  'subscription.downgrade_to_free_desc': 'This is immediate and you will NOT receive a refund for your remaining paid period. You will lose access to premium features.',
  'subscription.downgrade_plan_title': 'Downgrade Plan',
  'subscription.downgrade_plan_desc': 'Your unused time will be credited toward your next invoice. This change takes effect immediately.',

  // Profile - Additional
  'common.not_set': 'Not set',
  'profile.salary_not_set': 'Not configured',
  'profile.learn_features': 'Learn how to use Helpy',
  'profile.addMember': 'Add Family Member',
  'profile.share_with': 'Share with',
  'profile.creating_invite': 'Creating invite...',
  'profile.added': 'added',

  // Common - Additional (Translation Audit)
  'common.error': 'Error',
  'common.or': 'or',
  'common.continue': 'Continue',
  'common.confirm': 'Confirm',
  'common.got_it': 'Got it',
  'common.save_changes': 'Save Changes',
  'common.view_plan': 'View Plan',

  // Settings - Appearance (Translation Audit)
  'settings.appearance': 'Appearance',
  'settings.light': 'Light',
  'settings.dark': 'Dark',
  'settings.system': 'System',
  'settings.light_description': 'Always light',
  'settings.dark_description': 'Always dark',
  'settings.system_description': 'Follows your device',
  'settings.push_description_full': 'Get notified about family activity: Shopping, Tasks, Meals, Expenses, Family Board',
  'settings.notifications_blocked': 'Notifications are blocked. To enable them, go to your browser settings and allow notifications for this site.',

  // Subscription - Additional (Translation Audit)
  'subscription.trial_banner': 'Free Trial',
  'subscription.trial_ends': 'Free trial ends',
  'subscription.days_remaining': 'days remaining',
  'subscription.cancelled': 'Cancelled',
  'subscription.access_until': 'You have access until',
  'subscription.referral_code': 'Referral Code (for free trial)',
  'subscription.referral_code_placeholder': 'e.g., PROMOCODE',
  'subscription.referral_valid': '30-day free trial will be applied!',
  'subscription.promo_code': 'Promo code (optional)',
  'subscription.promo_code_placeholder': 'Enter promo code',
  'subscription.promo_code_hint': 'We will apply this code on the Stripe checkout page.',
  'subscription.code_label': 'Promo or Referral Code (optional)',
  'subscription.code_placeholder': 'e.g., SUMMER20',
  'subscription.promo_valid': 'Promo code will be applied at checkout.',
  'subscription.upgrade_to': 'You are about to upgrade to the {plan} plan.',

  // Profile - Additional (Translation Audit)
  'profile.start_date': 'Start Date',
  'profile.confirmDelete': 'Are you sure you want to delete this family member? This action cannot be undone.',
  'profile.remove_member': 'Remove from Family',
  'profile.remove_warning': 'This will permanently remove this person from your family.',
  'profile.google_managed': 'Your account is managed through Google. Password changes must be made through your Google account settings.',
  'profile.allowance_name_placeholder': 'Allowance name',

  // PWA Install (Translation Audit)
  'pwa.add_to_home': 'Add Helpy to Home Screen',
  'pwa.install_helpy': 'Install Helpy',
  'pwa.looks_like_app': 'It will look and feel like an app.',
  'pwa.ios_description': 'Helpy works best as an app. On iPhone, adding to Home Screen helps with notifications.',
  'pwa.android_description': 'Helpy works best as an app. Add to Home Screen for full screen experience and notifications.',
  'pwa.install_description': 'Open faster, full screen, and get a smoother notification setup.',
  'pwa.show_steps': 'Show steps',
  'pwa.install_app': 'Install app',
  // iOS steps
  'pwa.step_tap_share': 'Tap Share',
  'pwa.step_add_home': 'Add to Home Screen',
  'pwa.step_open_as_webapp': 'Switch on "Open as Web App" if available',
  'pwa.step_tap_add': 'Tap Add',
  'pwa.step_done': 'Done and open Helpy from your homescreen',
  // Android steps
  'pwa.step_tap_menu': 'Tap the menu',
  'pwa.step_menu_location': '(three dots in the top right corner)',
  'pwa.step_or_install': 'Or look for "Install app" option',
  'pwa.step_confirm_add': 'Confirm by tapping Add or Install',
  'pwa.add_to_homescreen': 'Add to Homescreen',
  'pwa.add_to_homescreen_now': 'Add to Homescreen now',
  'pwa.or_follow_steps': 'Or follow these steps:',
  'pwa.follow_steps': 'Follow these steps:',
  'pwa.desktop_instructions': 'Look for the install icon in your browser\'s address bar or menu to add Helpy to your desktop.',
  'pwa.welcome': 'Welcome!',
  'pwa.please_add_to_home': 'Please add Helpy to your home screen',
  'pwa.please_add_to_home_line1': 'Please add Helpy',
  'pwa.please_add_to_home_line2': 'to your home screen',

  // Error - Additional (Translation Audit)
  'error.account_setup_incomplete': 'Account Setup Incomplete',
  'error.please_logout_signin': 'Please try logging out and signing in again.',

  // Helper - Payslip Breakdown (Translation Audit)
  'helper.breakdown_base_salary': 'Base Salary',
  'helper.breakdown_other_allowances': 'Other Allowances',
  'helper.breakdown_overtime': 'Overtime',
  'helper.breakdown_calculated_total': 'Calculated Total',
  'helper.til_badge': 'TIL',

  // User Guide - Additional (Translation Audit)
  'guide.rsvp': 'RSVP',

  // Alert Modal - Common (added for native alert() replacement)
  'common.ok': 'OK',
  'common.clear_all': 'Clear All',
  'common.keep_subscription': 'Keep Subscription',
  'common.cancel_btn': 'Cancel',

  // Alert Modal - ToDo
  'todo.clear_completed_title': 'Clear Completed Items',

  // Alert Modal - Subscription
  'subscription.cancel_title': 'Cancel Subscription',
  'subscription.downgrade_success_title': 'Subscription Canceled',

  // Alert Modal - Maps (Family)
  'maps.open_in_maps': 'Open in Maps',
  'maps.open_apple_maps': 'Open Apple Maps',

  // Alert Modal - Error Titles
  'error.downgrade_title': 'Downgrade Failed',
  'error.portal_title': 'Portal Error',
  'error.add_user_title': 'Add User Failed',
  'error.invite_title': 'Invite Failed',
  'error.invalid_file_title': 'Invalid File',
  'error.file_too_large_title': 'File Too Large',
  'error.upload_failed_title': 'Upload Failed',
  'error.cancel_subscription_title': 'Cancellation Failed',
  'error.delete_account_title': 'Delete Failed',
  'error.invite_expired_title': 'Invitation Expired',
  'error.invite_expired': 'This invitation has expired. Please ask for a new invite link.',
  'error.email_conflict_title': 'Email Already Used',
  'error.email_conflict': 'This email is already associated with another Helpy account. If you already have an account, please sign in instead of signing up. Or use a different email address.',
  'error.invite_processing_title': 'Invitation Error',
  'error.invite_processing': 'There was a problem processing your invitation. Please try clicking the invite link again or contact the household admin for a new link.',
  'error.account_setup_title': 'Account Setup Failed',
  'error.unknown': 'Unknown error',
  'error.try_again': 'Please try again.',
  'error.create_household_title': 'Household Creation Failed',
  'error.create_household': 'Failed to create household',
  'error.switch_household_title': 'Household Switch Failed',
  'error.switch_household': 'Failed to switch household',
  'error.remove_user_title': 'Remove User Failed',
  'error.remove_user': 'Failed to remove user',

  // User-friendly error messages (replacing technical gibberish)
  'error.email_already_registered': 'This email is already registered. Please sign in instead.',
  'error.weak_password': 'Password must contain at least 8 characters, 1 lowercase, 1 uppercase, 1 special character',
  'error.incorrect_code': 'Incorrect code. Please check your email and enter the correct 6-digit code.',
  'error.code_expired': 'Code expired. Please request a new one.',
  'error.too_many_attempts': 'Too many attempts. Please wait a few minutes before trying again.',
  'error.ocr_busy': 'Receipt scanning is busy. Please try again in a moment.',
  'error.ocr_failed': 'Could not read this receipt. Please try a clearer photo.',
  'error.save_expense': 'Could not save expense. Please try again.',
  'error.update_expense': 'Could not update expense. Please try again.',
  'error.delete_expense': 'Could not delete expense. Please try again.',
  'error.connection_lost': 'Connection lost. Please check your internet and try again.',
  'error.account_setup': 'Account setup failed. Please try again or contact support.',
  'error.household_create': 'Could not create your household. Please try again.',
  'error.session_expired': 'Your session has expired. Please sign in again to continue.',
  'error.session_expired_title': 'Session Expired',
  'error.invalid_promo_code': 'This promo code is not valid or has expired.',
  'error.plan_change_failed': 'Could not change your plan. Please try again or contact support.',
  'error.subscription_settings': 'Could not open subscription settings. Please try again.',
  'error.update_amount': 'Could not update amount. Please try again.',
  'error.signup_failed': 'Sign up failed. Please try again.',
  'error.verification_failed': 'Verification failed. Please try again.',
  'error.title': 'Error',
  'error.taskSaveFailed': 'Failed to save task. Please try again.',

  // PWA Update Toast
  'update.new_version_title': 'New App Update',
  'update.available': 'Available',
  'update.update_button': 'Update',
  'common.dismiss': 'Dismiss',

  // Demo Mode (Marketing Screenshots)
  'demo.title': 'Demo Mode',
  'demo.description': 'Show sample data for marketing screenshots',
  'demo.enabled': 'Demo Mode Enabled',
  'demo.disabled': 'Demo Mode Disabled',
  'demo.superadmin_only': 'SuperAdmin Only',

  // Helper Payslip (new UI)
  'helper.payslip': 'Payslip',
  'helper.show_past_salary': 'Past Salary',
  'helper.hide_past_salary': 'Hide Past Salary',
  'helper.show_past_signed_salary': 'Past & Signed Salary',
  'helper.hide_past_signed_salary': 'Hide Past & Signed Salary',
  'helper.no_signed_payslips': 'No signed payslips yet',
  'helper.started': 'Started',

  // Salary Slip System
  'salary.contract': 'Employment Details',
  'salary.edit_contract': 'Edit Details',
  'salary.setup_contract': 'Set Up Details',
  'salary.no_contract': 'No details set up yet',
  'salary.start_date': 'Employment Start Date',
  'salary.base_salary': 'Base Salary',
  'salary.food_allowance': 'Food Allowance',
  'salary.monthly_total': 'Monthly Total',
  'salary.slips': 'Salary Slips',
  'salary.no_slips': 'No salary slips yet',
  'salary.create_slip': 'Create Salary Slip',
  'salary.confirm_slip': 'Confirm Salary Slip',
  'salary.confirm_message': 'A salary slip will be generated for:',
  'salary.extra_salary': 'Additional Pay',
  'salary.deduction': 'Pay Deduction',
  'salary.total_salary': 'Total Salary',
  'salary.payment_period': 'Payment Period',
  'salary.period_start': 'For Payment From',
  'salary.period_end': 'For Payment To',
  'salary.note': 'Note',
  'salary.note_placeholder': 'Optional notes...',
  'salary.helper_name': 'Helper Name',
  'salary.select_helper': 'Select helper',
  'salary.from_contract': 'From employment details',
  'salary.no_contract_warning': 'No employment details found. Please set up details first.',
  'salary.deduction_hint': 'Enter as positive number, will be subtracted',
  'salary.employer': 'Employer',
  'salary.helper': 'Helper',
  'salary.signed': 'Signed',
  'salary.not_signed': 'Not signed',
  'salary.sign': 'Sign',
  'salary.select_signer': 'Select signer',
  'salary.confirm_sign': 'Confirm & Sign',
  'salary.sign_confirm_title': 'Confirm Signature',
  'salary.sign_confirm_message': 'This action CANNOT be reversed. Please make sure you have checked everything before signing.',
  'salary.delete_confirm_title': 'Delete Salary Slip?',
  'salary.delete_confirm_message': 'This action cannot be undone. The salary slip will be permanently deleted.',
  'salary.export_pdf': 'Export PDF',
  'salary.show_signed': 'Past & Signed Slips',
  'salary.hide_signed': 'Hide Signed Slips',
  'salary.slip_title': 'Salary Slip',
  'salary.started': 'Started',
  'salary.breakdown': 'Salary Breakdown',
  'salary.signatures': 'Signatures',
  'salary.export_all': 'Export All',
  'salary.past_signed': 'Past & Signed Slips',
  'salary.awaiting_signature': 'Awaiting signature',
  'salary.edit_note': 'Changes to salary and food allowance will only apply to new salary slips. Existing slips are not affected.',
  'salary.info_title': 'Important Information',
  'salary.info_message': 'Salary records will be removed if you delete the helper user. If you would like to keep the salary records, please use the Export All function to download all slips before deletion.',

  // Salary Slip Reminder Notification
  'salary_reminder.title': "Don't forget!",
  'salary_reminder.description': "It's nearly the end of the month, use Helpy to create a salary slip for your helper that can be e-signed!",
  'salary_reminder.show_me_how': 'Show me how',
  'salary_reminder.remind_later': 'Remind me Later',

  // ToDo - Section Labels
  'tasks.section.overdue': 'Overdue',
  'tasks.section.noDate': 'No Due Date',
  'tasks.section.today': 'Today',
  'tasks.section.tomorrow': 'Tomorrow',
  'tasks.section.thisWeek': 'This Week',
  'tasks.section.nextWeek': 'Next Week',
  'tasks.section.later': 'Later',

  // Practice Ideas (Suggested Practice Templates)
  'info.practice_ideas': 'Practice Ideas',
  'info.practice_ideas_title': 'Practice Ideas',
  'info.practice_ideas_subtitle': 'Choose from commonly used templates to help organize your home.',
  'info.practice_ideas_disclaimer': 'These templates are provided for convenience and inspiration. Please review and customize them to fit your family\'s needs and comply with local regulations.',
  'info.practice_ideas_select_all': 'Select All',
  'info.practice_ideas_deselect_all': 'Deselect All',
  'info.practice_ideas_add_selected': 'Add Selected',
  'info.practice_ideas_adding': 'Adding...',
  'info.practice_ideas_all_added': 'You\'ve added all suggested ideas!',
  'info.practice_ideas_all_added_desc': 'Great job setting up your household practices.',
  'info.practice_ideas_selected': 'selected',
  'info.practice_ideas_confirm_title': 'Add {count} Practice Ideas?',
  'info.practice_ideas_confirm_desc': 'You can edit or delete them anytime after adding.',

  // Practice Ideas - Preset Names
  'practice.preset.hk-rules-phone.name': 'Phone Usage',
  'practice.preset.hk-rules-visitor.name': 'Visitor Policy',
  'practice.preset.hk-rules-privacy.name': 'Social Media & Privacy',
  'practice.preset.hk-rules-leaving.name': 'Leaving the House',
  'practice.preset.hk-rules-keys.name': 'Key Security',
  'practice.preset.hk-safety-window.name': 'Window Cleaning Safety',
  'practice.preset.hk-safety-door.name': 'Door Security',
  'practice.preset.hk-safety-cooking.name': 'Cooking Safety',
  'practice.preset.hk-safety-chemicals.name': 'Chemical Storage',
  'practice.preset.hk-clean-cloths.name': 'Color-Coded Cloths',
  'practice.preset.hk-clean-trash.name': 'Trash Disposal',
  'practice.preset.hk-clean-deepclean.name': 'Deep Cleaning Areas',
  'practice.preset.hk-clean-linen.name': 'Bedding & Towels',
  'practice.preset.hk-cook-hygiene.name': 'Food Hygiene',
  'practice.preset.hk-cook-grocery.name': 'Grocery Alerts',
  'practice.preset.hk-cook-leftovers.name': 'Leftover Labeling',
  'practice.preset.hk-cook-kitchen.name': 'Kitchen Hygiene',
  'practice.preset.hk-child-supervision.name': 'Child Supervision',
  'practice.preset.hk-child-diet.name': 'Dietary Restrictions',
  'practice.preset.hk-child-hygiene.name': 'Hand Hygiene',
  'practice.preset.hk-child-reporting.name': 'Health Reporting',
  'practice.preset.hk-laundry-sorting.name': 'Laundry Sorting',
  'practice.preset.hk-laundry-delicates.name': 'Delicate Items',
  'practice.preset.hk-laundry-ironing.name': 'Ironing Guidelines',
  'practice.preset.hk-helper-health.name': 'Health First',
  'practice.preset.hk-helper-feedback.name': 'Open Communication',
  'practice.preset.hk-helper-rest.name': 'Rest Period',
  'practice.preset.hk-helper-wifi.name': 'WiFi Access',
  'practice.preset.hk-routine-priority.name': 'Task Priorities',
  'practice.preset.hk-routine-endofday.name': 'End of Day Checklist',

  // Practice Ideas - Preset Notes
  'practice.preset.hk-rules-phone.note': 'Personal mobile phone usage is restricted to break times and emergencies. Please avoid using it while performing active tasks or caring for children.',
  'practice.preset.hk-rules-visitor.note': 'No friends or relatives are allowed to enter the home without explicit permission from the employer.',
  'practice.preset.hk-rules-privacy.note': "Do not post photos or videos of the home, family members, or children on social media platforms to protect the family's privacy.",
  'practice.preset.hk-rules-leaving.note': 'Always inform the employer before leaving the flat. Please return home by the agreed curfew on rest days.',
  'practice.preset.hk-rules-keys.note': 'Household keys are for your personal use only. Do not duplicate them or lend them to anyone else.',
  'practice.preset.hk-safety-window.note': 'Only clean the interior of windows. Exterior cleaning is strictly prohibited unless there are fixed grilles and you remain on the floor.',
  'practice.preset.hk-safety-door.note': 'Keep the main door locked at all times. Do not open the door for strangers, delivery people, or technicians unless pre-arranged by the employer.',
  'practice.preset.hk-safety-cooking.note': 'Never leave the stove unattended while it is on. Ensure the gas/electricity is switched off immediately after use.',
  'practice.preset.hk-safety-chemicals.note': "Store all cleaning detergents and chemicals in the designated high cabinet, away from food and out of children's reach.",
  'practice.preset.hk-clean-cloths.note': 'Use specific colored cloths for different areas: Red for toilets, Blue for the kitchen, and White for dining surfaces.',
  'practice.preset.hk-clean-trash.note': 'Empty all household trash bins before finishing work. Separate recyclables (paper, plastic, metal) from general waste.',
  'practice.preset.hk-clean-deepclean.note': 'Areas that need regular deep cleaning: under beds and sofas, inside microwave and oven, behind furniture, and inside the fridge.',
  'practice.preset.hk-clean-linen.note': 'Bedsheets, pillowcases, and towels should be changed regularly. Use hot water for white linens and cold water for colored items.',
  'practice.preset.hk-cook-hygiene.note': 'Use separate cutting boards and knives for raw meat and vegetables to prevent cross-contamination.',
  'practice.preset.hk-cook-grocery.note': 'Please update the Shopping List in Helpy when staples (rice, oil, milk) are about 20% remaining.',
  'practice.preset.hk-cook-leftovers.note': 'Store leftovers in airtight containers. Label them with the date before putting them in the fridge.',
  'practice.preset.hk-cook-kitchen.note': 'Clean the sink and wipe down the stovetop immediately after every meal preparation.',
  'practice.preset.hk-child-supervision.note': 'Never leave children unattended, especially in the bathroom, on high chairs, or at the public playground.',
  'practice.preset.hk-child-diet.note': 'Do not give the children candy, chocolate, or juice without checking with the parents first.',
  'practice.preset.hk-child-hygiene.note': 'Ensure children wash their hands immediately upon returning home from school or the park.',
  'practice.preset.hk-child-reporting.note': 'Inform parents immediately if a child has a fever, a fall/injury, or displays unusual behavior.',
  'practice.preset.hk-laundry-sorting.note': 'Separate whites, darks, and colors before washing. Check all pockets before loading the machine.',
  'practice.preset.hk-laundry-delicates.note': 'Hand wash delicate items or use laundry bags. Do not put wool, silk, or items with beading in the dryer.',
  'practice.preset.hk-laundry-ironing.note': 'Work shirts and school uniforms should be ironed and hung ready before they are needed. Check care labels for temperature settings.',
  'practice.preset.hk-helper-health.note': 'Please inform us immediately if you feel unwell or are injured so we can arrange medical care.',
  'practice.preset.hk-helper-feedback.note': 'We value open communication. Please feel free to discuss any concerns, questions, or suggestions with us at any time.',
  'practice.preset.hk-helper-rest.note': 'We respect your 8 hours of uninterrupted sleep. Unless it is an emergency, we will not call for you after 10:00 PM.',
  'practice.preset.hk-helper-wifi.note': 'You are welcome to use the home WiFi during your rest periods and breaks. Ask us for the password.',
  'practice.preset.hk-routine-priority.note': 'Priority order: Child safety first, then meal preparation, followed by cleaning tasks. Urgent requests from employers take precedence.',
  'practice.preset.hk-routine-endofday.note': 'Before finishing work: ensure kitchen is clean, trash is emptied, doors are locked, and children have completed their evening routine.',

  // Error Messages - Family (Places/Practice)
  'error.save_place': 'Failed to save. Please try again.',
  'error.delete_place': 'Failed to delete. Please try again.',
  'error.save_practice': 'Failed to save. Please try again.',
  'error.delete_practice': 'Failed to delete. Please try again.',

  // Error Messages - Helper/Salary
  'error.salary_load_data': 'Failed to load data. Please try again.',
  'error.required_fields': 'Please fill in all required fields',
  'error.salary_delete_slip': 'Failed to delete salary slip. Please try again.',
  'error.sign_slip': 'Failed to sign. Please try again.',
  'error.select_helper': 'Please select a helper',
  'error.select_dates': 'Please select payment period dates',
  'error.enter_salary': 'Please enter a valid base salary',
  'error.invalid_dates': 'End date must be after start date',
  'error.create_slip': 'Failed to create salary slip. Please try again.',

  // Common - Additional
  'common.proceed': 'Proceed',
  'common.view_plans': 'View Plans',
  'common.upgrade_required': 'Upgrade',

  // Helper Section - Upgrade Messages
  'helper.upgrade_required_title': 'Helper Management',
  'helper.upgrade_required_desc': 'Upgrade to Core or Pro to access Helper Management features',

  // Trial Period Messages (Time-based - Spending Summary)
  'trial.warning_title': 'Trial Ending Soon',
  'trial.warning_day13': 'Your spending summary trial ends tomorrow!',
  'trial.warning_day14': 'Your spending summary trial ends today.',
  'trial.expiring_today_title': 'Trial Ends Today',
  'trial.expired_title': 'Trial Ended',
  'trial.expired': 'Your free trial has ended.',
  'trial.expired_upgrade_prompt': 'Upgrade to Core or Pro to unlock spending summaries.',
  'trial.upgrade_prompt': 'Subscribe to continue viewing spending summaries.',
  'trial.continue_button': 'Continue',
  'trial.upgrade_button': 'View Plans',
  'trial.days_remaining': '{days} days remaining',
  'trial.banner_message': 'Free trial: {days} days left',

  // Usage-Based Trial Messages - AI Scanner
  'trial.ai_scan_remaining': '{count} free scans remaining',
  'trial.ai_scan_limit_reached': 'You\'ve used all 5 free scans',
  'trial.ai_scan_upgrade_title': 'Scan Limit Reached',
  'trial.ai_scan_upgrade_desc': 'Upgrade to Core or Pro for unlimited receipt scanning.',
  'trial.ai_scan_badge': '{used} of {total} free scans used',

  // Usage-Based Trial Messages - Salary Slip Signature
  'trial.salary_sign_free': 'First signature is free',
  'trial.salary_sign_limit_reached': 'Free signature used',
  'trial.salary_sign_upgrade_title': 'Upgrade Required',
  'trial.salary_sign_upgrade_desc': 'Upgrade to Core or Pro to sign more salary slips.',
  'trial.salary_sign_first_free': 'Your first signature is free!',

  // Usage-Based Trial Messages - Spending Summary
  'trial.spending_summary_days': '{days} days remaining',
  'trial.spending_summary_expired': 'Spending summary trial ended',
  'trial.spending_summary_upgrade_title': 'Trial Ended',
  'trial.spending_summary_upgrade_desc': 'Upgrade to Core or Pro to view monthly spending summaries.',

  // Pending Helper Notification
  'notification.pending_helper': 'Oh no! It looks like {name} has not activated their account yet. Would you like to resend the invite?',
  'notification.resend': 'Resend',
  'notification.resend_success': 'Invite resent successfully!',
  'notification.resend_error': 'Failed to resend invite. Please try again.',
};